// Proof of Delivery Upload Screen
import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import ProofOfDeliveryServices from '../../services/ProofOfDelivery';

const ProofOfDelivery = () => {
  const [orderCode, setOrderCode] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      try {
        codeReader.current.reset();
        if (videoRef.current && videoRef.current.srcObject) {
          const stream = videoRef.current.srcObject;
          const tracks = stream.getTracks();
          tracks.forEach(track => track.stop());
        }
      } catch (error) {
        console.error('Error cleaning up camera:', error);
      }
    };
  }, []);

  const fetchOrderDetails = async () => {
    if (!orderCode.trim()) {
      setMessage('Please enter an order code');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const data = await ProofOfDeliveryServices.getOrderDetails(orderCode);
      setOrderDetails(data);
    } catch (error) {
      setMessage('Order not found or failed to fetch order details');
      setOrderDetails(null);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (!orderDetails || files.length === 0) {
      setMessage('Please select files to upload');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await ProofOfDeliveryServices.uploadFiles(orderCode, files);
      setMessage('Files uploaded successfully!');
      setFiles([]);
      setOrderCode(''); // Reset order code
      setOrderDetails(null); // Reset order details
    } catch (error) {
      setMessage('Failed to upload files');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startScanning = async () => {
    setScanning(true);
    setMessage('');

    try {
      // Request camera permission and get video stream
      const videoInputDevices = await codeReader.current.getVideoInputDevices();
      if (videoInputDevices.length === 0) {
        throw new Error('No camera devices found');
      }

      // Use the first available camera
      const selectedDeviceId = videoInputDevices[0].deviceId;

      const result = await codeReader.current.decodeOnceFromVideoDevice(selectedDeviceId, videoRef.current);
      if (result) {
        setOrderCode(result.getText());
        // Stop scanning and release camera
        stopScanning();
        // Automatically fetch order details
        setTimeout(() => fetchOrderDetails(), 500);
      }
    } catch (error) {
      console.error('QR scan failed:', error);
      if (error.name === 'NotAllowedError') {
        setMessage('Camera access denied. Please allow camera permissions and try again.');
      } else if (error.name === 'NotFoundError') {
        setMessage('No camera found. Please connect a camera and try again.');
      } else {
        setMessage(`Failed to scan QR code: ${error.message}`);
      }
      setScanning(false);
    }
  };

  const stopScanning = () => {
    try {
      codeReader.current.reset();
      // Stop all video tracks to release camera
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    } catch (error) {
      console.error('Error stopping camera:', error);
    }
    setScanning(false);
  };

  const handleScanQR = () => {
    if (scanning) {
      stopScanning();
    } else {
      startScanning();
    }
  };

  return (
    <div className="container-fluid p-3" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div className="text-center mb-4">
        <h2>Proof of Delivery</h2>
        <p>Upload delivery confirmation documents</p>
      </div>

      {message && (
        <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-danger'} mb-3`}>
          {message}
        </div>
      )}

      {/* Order Code Input */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Enter order code"
              value={orderCode}
              onChange={(e) => setOrderCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchOrderDetails()}
            />
            <button
              className={`btn ${scanning ? 'btn-danger' : 'btn-outline-secondary'}`}
              type="button"
              onClick={handleScanQR}
            >
              {scanning ? 'Stop Scan' : '📱 Scan QR'}
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={fetchOrderDetails}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Search'}
            </button>
          </div>

          {/* QR Scanner Video */}
          {scanning && (
            <div className="mt-3">
              <video
                ref={videoRef}
                style={{ width: '100%', maxHeight: '300px', border: '1px solid #ccc' }}
                playsInline
                muted
              />
              <p className="text-center mt-2 text-muted">Position QR code in front of camera</p>
            </div>
          )}
        </div>
      </div>

      {/* Order Details */}
      {orderDetails && (
        <div className="card mb-3">
          <div className="card-header">
            <h5>Order Details</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-6">
                <strong>Order:</strong> {orderDetails.order_number}
              </div>
              <div className="col-6">
                <strong>Customer:</strong> {orderDetails.customer_name}
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-6">
                <strong>Delivery Date:</strong> {new Date(orderDetails.delivery_date).toLocaleDateString()}
              </div>
              <div className="col-6">
                <strong>Time Slot:</strong> {orderDetails.delivery_time_slot}
              </div>
            </div>
            <div className="mt-2">
              <strong>Address:</strong> {orderDetails.delivery_address}
            </div>
            <div className="mt-2">
              <strong>Items:</strong>
              <ul className="list-unstyled mt-1">
                {orderDetails.items?.map((item, index) => (
                  <li key={index}>• {item.name} (Qty: {item.quantity})</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* File Upload */}
      {orderDetails && (
        <div className="card mb-3">
          <div className="card-body">
            <h5>Upload Files</h5>
            <div className="mb-3">
              <input
                type="file"
                className="form-control"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
              />
              <small className="text-muted">Supported formats: Images, PDF, Word documents</small>
            </div>

            {files.length > 0 && (
              <div className="mb-3">
                <strong>Selected files:</strong>
                <ul className="list-group mt-2">
                  {files.map((file, index) => (
                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      {file.name}
                      <span className="badge bg-primary rounded-pill">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              className="btn btn-success w-100"
              onClick={handleUpload}
              disabled={loading || files.length === 0}
            >
              {loading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProofOfDelivery;